import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { AddTab, AnimatedProgressIndicator, MoveLeft, MoveRight, TabOverflow } from '@sas/react-svg';
import cx from 'classnames';
import { TabBarVariant, TabDragType } from '.';
import { Tab } from '../__internal__/index';
import { setAlpha } from '../../../design-tokens';
import { useReactDndConstructs as useDragAndDropHooks } from '../../../dnd/ReactDndContext';
import { EarconName, useEarcon } from '../../../hooks/useEarcon';
import { createUseHtmlId } from '../../../hooks/useHtmlId/index';
import { usePreviousValue } from '../../../hooks/usePreviousValue/index';
import { useRefArray } from '../../../hooks/useRefArray';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useIsRTL } from '../../../locale/index';
import { getDndDesignTokens } from '../../../style/reactDnd/DndStyleTokens';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { InverseTokenProvider, renderInlineCssVars, useInvertedThemeData } from '../../../theme/index';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId/index';
import { Button, ButtonVariant } from '../../Button/index';
import { useDensity } from '../../Density';
import { InvisibleText } from '../../InvisibleText';
import { MenuButton } from '../../MenuButton';
import { BaseScrollable } from '../../Scrollable/__internal__';
import { DraggableTab } from './dnd/DraggableTab';
import { TabDragPreview } from './dnd/TabDragPreview';
import bundle from '../NovaComponents-Tab-gui-icu.properties';
import cn from './Tab.css.json';
import './Tab.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useTabId = createUseHtmlId('TabBar-tab');
const useTabbarDndPrefixId = createUseHtmlId('TabBar-dndPrefix');
const useTabAriaDescribedById = createUseHtmlId('Tab-aria-description');
export const BaseTabBar = /*#__PURE__*/forwardRef(function BaseTabBar(props, ref) {
  const {
    useDrop
  } = useDragAndDropHooks();
  const localizer = useTextLocalizer();
  const alternate = props.visualStyle === 'alternative';
  const variant = props.variant ?? TabBarVariant.Standard;
  const themeTransition = props.themeTransition === true;
  const invertedThemeContext = useInvertedThemeData();
  const {
    tokenOverrides,
    onItemSelected,
    focusedIndex,
    setFocusedIndex
  } = props;
  const orientation = props.orientation ?? 'horizontal';
  const placement = props.placement ?? 'start';
  const stretchToFit = props.stretchToFit ?? false;
  const isVerticalOriented = orientation === 'vertical';
  const renderTabToolbar = props.renderTabToolbar;
  const role = props.role ? props.role : !props.addNewTabButtonProps && !props.expandCollapseProps && !props.onTabMenuItemSelect ? undefined : 'group';
  const isRTL = useIsRTL();
  const ariaDescriptionId = useTabAriaDescribedById();
  const [areScrollButtonsVisible, setScrollButtonVisibility] = useState(false);
  const [isOverNonScrollableZone, setIsOverNonScrollableZone] = useState(false);
  const [overflowMenuSize, setOverflowMenuSize] = useState(0);
  const [addButtonSize, setAddButtonSize] = useState(0);
  const [scrollListSize, setScrollListSize] = useState(0);
  const [tabBarSize, setTabBarSize] = useState(0);
  const [preventInitialAutoFocus, setPreventInitialAutoFocus] = useState(props.preventInitialAutoFocus ?? false);
  const {
    refs: itemRefs,
    createSetRef: createItemRef
  } = useRefArray();
  useImperativeHandle(ref, () => ({
    ...tabBarRef.current,
    focus: () => {
      if (itemRefs && itemRefs[props.selectedIndex]) {
        setTimeout(() => {
          itemRefs[props.selectedIndex]?.focus();
        }, 0);
      }
    }
  }));

  /* ------------------------------------------------------------
   * Set Aria Label and tooltip for expand/collapse buttons
   * ------------------------------------------------------------ */
  const expandTooltip = props.expandCollapseProps?.expandTooltip ?? localizer('tabBar.default.expanded.label');
  const collapseTooltip = props.expandCollapseProps?.collapseTooltip ?? localizer('tabBar.default.collapsed.label');
  const expandAriaLabel = props.expandCollapseProps?.expandAriaLabel ?? expandTooltip;
  const collapseAriaLabel = props.expandCollapseProps?.collapseAriaLabel ?? collapseTooltip;
  const overflowMenuButtonPosition = props.overflowMenuButtonPosition ?? 'endOfTabList';
  const defaultDnDPrefix = useTabbarDndPrefixId();
  const dndPrefix = props.dndPrefix ?? defaultDnDPrefix;
  const dragType = props.dragType ?? TabDragType;
  const droppableTabs = props.dropTypes?.tabs ?? [TabDragType];
  const droppableContent = props.dropTypes?.content ?? [];
  const acceptDropTypes = Array.from(new Set([dragType, ...droppableTabs, ...droppableContent]));
  const tabBarRef = useRef(null);
  const tabBarScrollableRef = useRef(null);
  const addTabBtnRef = useRef(null);
  const overflowMenuRef = useRef(null);

  // FIXME: TabBar no longer requires overrides. Keeping this in place for now to prevent build issues.
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const tokens = {
    ...getTabBarDesignTokens(dt, {
      alternate,
      variant,
      tokenOverrides,
      density
    }),
    ...props.theme?.(dt, {
      alternate,
      variant
    })
  };
  const dndTokens = getDndDesignTokens(dt);
  // End fixme

  const selectIfNeeded = useCallback((index, item) => {
    setTabItemHasFocus(true);
    onItemSelected({
      index,
      item
    });
    setFocusedIndex(index);
  }, [onItemSelected, setFocusedIndex]);
  const [tabItemHasFocus, setTabItemHasFocus] = useState(false);
  const [horizontalScrollIndex, setHorizontalScrollIndex] = useState(props.selectedIndex < 0 ? focusedIndex : props.selectedIndex);

  // Check if any item is movable
  const isItemsMovable = useMemo(() => {
    return props.items.some(item => item.allowDrag);
  }, [props.items]);
  const shouldDisplayContextMenu = props.editable || props.items.some(item => item.closable || item.menuSections && item.menuSections.length > 0);
  const areAllTabsCloseable = props.items.every(item => item.closable);
  const prevItems = usePreviousValue(props.items);
  const getStretchToFitTabBarItemWidth = renderInlineCssVars({
    'stretched-width': !areScrollButtonsVisible && tabBarScrollableRef.current && props.stretchToFit ? `${Math.floor(tabBarScrollableRef.current.clientWidth / props.items.length)}px` : undefined
  }, 'tab-item');
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  useEffect(() => {
    /**
     * While the default value of the preventInitialAutoFocus prop is undefined/false,
     *    it can be set to true if the initial focus on the tabbar is not desired.
     * But to enable keyboard navigation in the tabbar, the preventInitialAutoFocus value should be set to false,
     *    hence the value gets set to false at Tab.tsx during initial rendering.
     * However, if the items get changed,
     *    the preventInitialAutoFocus value should be reset to true to prevent unexpected focus behavior.
     */
    if (props.preventInitialAutoFocus) {
      setPreventInitialAutoFocus(props.preventInitialAutoFocus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.items]);
  useEffect(() => {
    if (props.selectedIndex > -1) {
      setFocusedIndex(props.selectedIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedIndex, setFocusedIndex]);
  useEffect(() => {
    if (!isNullish(prevItems) && prevItems.length !== props.items.length) {
      setHorizontalScrollIndex(props.selectedIndex);
      const timeoutId = setTimeout(() => {
        if (props.selectedIndex > -1) setFocusedIndex(props.selectedIndex);
      }, 0);
      setTabItemHasFocus(true);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [prevItems, props.items, props.selectedIndex, setFocusedIndex]);
  const onKeyDown = e => {
    const length = props.items.length;
    if (length <= 0) {
      return;
    }
    let preventDefault = true;
    const ctrl = e.ctrlKey;
    const shift = e.shiftKey;
    const allowDrag = props.items[horizontalScrollIndex].allowDrag;
    const disabled = props.items[horizontalScrollIndex].disabled;
    switch (e.key) {
      case 'ArrowDown' /* falls-through */:
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        if (isNullish(focusedIndex) || ctrl && shift && !allowDrag) {
          setFocusedIndex(focusedIndex);
        } else {
          if (focusedIndex < length - 1) {
            if (ctrl && shift && allowDrag && !disabled) {
              props.reorderTabs?.(horizontalScrollIndex, focusedIndex + 1);
            }
            setFocusedIndex(focusedIndex + 1);
          } else {
            if (ctrl && shift && allowDrag && !disabled) {
              props.reorderTabs?.(horizontalScrollIndex, focusedIndex);
            }
            setFocusedIndex(focusedIndex);
            if (focusedIndex + 1 === length) earcon.play();
          }
        }
        break;
      case 'ArrowUp' /* falls-through */:
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        if (isNullish(focusedIndex) || ctrl && shift && !allowDrag) {
          setFocusedIndex(focusedIndex);
        } else {
          if (focusedIndex > 0) {
            if (ctrl && shift && allowDrag && !disabled) {
              props.reorderTabs?.(horizontalScrollIndex, focusedIndex - 1);
            }
            setFocusedIndex(focusedIndex - 1);
          } else {
            if (ctrl && shift && allowDrag && !disabled) {
              props.reorderTabs?.(horizontalScrollIndex, focusedIndex);
            }
            setFocusedIndex(focusedIndex);
            if (focusedIndex === 0) earcon.play();
          }
        }
        break;
      case 'Home':
        if (ctrl && shift && allowDrag && !disabled) {
          props.reorderTabs?.(horizontalScrollIndex, 0);
        }
        setFocusedIndex(0);
        if (focusedIndex === 0) earcon.play();
        break;
      case 'End':
        if (ctrl && shift && allowDrag && !disabled) {
          props.reorderTabs?.(horizontalScrollIndex, length - 1);
        }
        setFocusedIndex(length - 1);
        if (focusedIndex + 1 === length) earcon.play();
        break;
      case ' ': /* falls-through */
      case 'Enter':
        if (!props.items[focusedIndex].disabled) onItemSelected?.({
          index: focusedIndex,
          item: props.items[focusedIndex]
        });
        break;
      case 'PageUp':
        setFocusedIndex(0);
        if (focusedIndex === 0) earcon.play();
        break;
      case 'PageDown':
        setFocusedIndex(length - 1);
        if (focusedIndex + 1 === length) earcon.play();
        break;
      default:
        preventDefault = false;
        break;
    }
    if (preventDefault) {
      e.preventDefault();
    }
  };

  // Keeping the implementation same as focus out hook.
  // This approach works well to detect focus out when
  // nested popovers/menus are involved.
  const timeoutId = useRef(null);
  const onBlur = () => {
    if (!timeoutId.current) {
      timeoutId.current = setTimeout(() => {
        setTabItemHasFocus(false);
        props.selectedIndex > -1 && setFocusedIndex(props.selectedIndex);
      }, 0);
    }
  };
  const onFocus = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  };
  const isTabFocusable = index => {
    return index === focusedIndex;
  };
  const defaultTabIds = useTabId(props.items.length);
  const tabIds = props.items.map(({
    id
  }, index) => id ?? defaultTabIds[index]);
  const onOverflowItemSelected = index => {
    setHorizontalScrollIndex(index);
    props.onItemSelected({
      index,
      item: props.items[index]
    });
  };
  const onOverflowItemClosed = index => {
    props.onTabClose?.(index);
  };
  const [{
    isOverFlowMenu
  }, overFlowBtnDropConnect] = useDrop({
    accept: acceptDropTypes,
    canDrop: () => false,
    collect(monitor) {
      let isOverFlowMenu = false;
      if (monitor.isOver() && monitor.getItem().dndPrefix !== dndPrefix && !droppableContent.includes(monitor.getItem()?.type)) {
        isOverFlowMenu = true;
      }
      return {
        isOverFlowMenu
      };
    }
  });
  const updateIsOverNonScrollableZoneState = monitor => {
    if (props.allowDrop) {
      let overNonScrollableZone = false;
      if (monitor.canDrop() && monitor.isOver() && !areScrollButtonsVisible && !droppableContent.includes(monitor.getItem()?.type)) {
        const contentSize = scrollListSize + addButtonSize;
        if (!isVerticalOriented && monitor.getItem().dndPrefix !== dndPrefix) {
          if (isRTL && monitor.getClientOffset().x < contentSize) {
            overNonScrollableZone = true;
          } else if (!isRTL && monitor.getClientOffset().x > contentSize) {
            overNonScrollableZone = true;
          }
        } else if (isVerticalOriented && monitor.getItem().dndPrefix !== dndPrefix && monitor.getClientOffset().y > contentSize) {
          overNonScrollableZone = true;
        }
      }
      setIsOverNonScrollableZone(overNonScrollableZone);
    }
  };
  const [{}, nonScrollZoneDropConnect] = useDrop({
    accept: acceptDropTypes,
    canDrop: item => {
      return item.dndPrefix !== dndPrefix ? props.allowDrop ?? true : false;
    },
    drop: (item, monitor) => {
      if (!isOverFlowMenu && !areScrollButtonsVisible && !monitor.didDrop() && dndPrefix !== item.dndPrefix && !droppableContent.includes(monitor.getItem()?.type) && !monitor.getDropResult()?.isBaseScrollableButtons) {
        props.onDrop?.({
          droppedItem: item,
          dropIndex: props.items.length,
          monitor
        });
      }
    },
    collect(monitor) {
      updateIsOverNonScrollableZoneState(monitor);
      return {};
    },
    hover: (_, monitor) => {
      updateIsOverNonScrollableZoneState(monitor);
    }
  });
  nonScrollZoneDropConnect(tabBarRef);
  const [{
    isOverTabBarScrollable
  }, tabBarScrollDropZoneConnect] = useDrop({
    accept: acceptDropTypes,
    canDrop: item => {
      return item.dndPrefix !== dndPrefix ? props.allowDrop ?? true : false;
    },
    collect(monitor) {
      let isOver = false;
      if (props.allowDrop && monitor.isOver() && !areScrollButtonsVisible && !droppableContent.includes(monitor.getItem()?.type)) {
        isOver = true;
      }
      return {
        isOverTabBarScrollable: isOver
      };
    }
  });
  tabBarScrollDropZoneConnect(tabBarScrollableRef);
  const [{
    isOverAddTabBtn,
    newTabDropMonitor
  }, addTabBtnDropConnect] = useDrop({
    accept: acceptDropTypes,
    canDrop: item => item.dndPrefix !== dndPrefix ? props.allowDrop ?? true : false,
    drop: (item, monitor) => {
      if (!monitor.didDrop() && dndPrefix !== item.dndPrefix) {
        props.onDrop?.({
          droppedItem: item,
          dropIndex: props.items.length,
          monitor
        });
      }
    },
    collect(monitor) {
      let isOverAddTabBtn = false;
      if (monitor.isOver() && monitor.getItem().dndPrefix !== dndPrefix) {
        isOverAddTabBtn = true;
      }
      return {
        isOverAddTabBtn,
        newTabDropMonitor: monitor
      };
    }
  });
  const isTabbarIconOnly = useMemo(() => {
    return props.items.some(item => {
      return !item.label;
    });
  }, [props.items]);
  const isButtonTextVisible = useMemo(() => {
    let visible = false;
    if (isVerticalOriented) {
      visible = true;
      if (props.expandCollapseProps && props.expandCollapseProps?.isExpanded === false) {
        visible = false;
      }
    }
    return visible;
  }, [isVerticalOriented, props.expandCollapseProps]);
  useEffect(() => {
    const eventParams = {
      droppedItem: newTabDropMonitor.getItem(),
      monitor: newTabDropMonitor,
      droppableType: 'ADD_BUTTON'
    };
    if (isOverAddTabBtn) {
      props.onDragOver?.(eventParams);
    } else {
      props.onDragLeave?.(eventParams);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverAddTabBtn]);
  addTabBtnDropConnect(addTabBtnRef);
  const getOverflowMenuSize = position => {
    const displayOverflowMenu = shouldOverflowMenuBeDisplayed(props.overflowMenuButtonDisplay ?? 'never', areScrollButtonsVisible);
    return displayOverflowMenu && position === overflowMenuButtonPosition ? overflowMenuSize : 0;
  };
  const dropZoneAreaStartPosition = renderInlineCssVars({
    'start-position': isRTL ? `${getOverflowMenuSize('endOfTabBar')}px` : `calc(${scrollListSize}px + ${addButtonSize}px + ${getOverflowMenuSize('endOfTabList')}px - ${tabBarSize}px)`,
    'end-position': isRTL ? `calc(${tabBarSize}px - ${scrollListSize}px + ${addButtonSize}px + ${getOverflowMenuSize('endOfTabList')}px)` : `${getOverflowMenuSize('endOfTabBar')}px`
  }, 'drop-zone');
  const dropZoneAreaStartVerticalPosition = renderInlineCssVars({
    'start-position': `calc(${scrollListSize}px + ${addButtonSize}px + ${getOverflowMenuSize('endOfTabList')}px )`,
    'end-position': `${getOverflowMenuSize('endOfTabBar')}px`
  }, 'drop-zone');
  function renderOverflowMenuButton() {
    const overflowMenuButtonTooltip = props.overflowMenuButtonTooltip ?? localizer('tabBar.overflowMenuButton.label');
    const overflowMenuButtonAriaLabel = props.overflowMenuButtonAriaLabel ?? overflowMenuButtonTooltip;
    let isTabsWithLabel = false;
    const overflowMenuSection = {
      items: props.items.map((item, index) => {
        if (item.label) {
          isTabsWithLabel = true;
        }
        return {
          text: item.label ?? item.icon?.tooltip ?? '',
          checked: index === props.selectedIndex,
          closable: !!item.closable,
          closeIconTooltip: props.closeTabButtonTooltip,
          icon: item.state === 'processing' ? AnimatedProgressIndicator : item.icon
        };
      })
    };
    const showOverflowButtonLabel = isTabsWithLabel && isButtonTextVisible;
    const buttonProps = {
      testId: postfixTestId(props.testId, 'overflowMenu'),
      icon: {
        icon: TabOverflow
      },
      text: showOverflowButtonLabel ? localizer('tabBar.overflowMenuButton.label') : undefined,
      tooltip: !showOverflowButtonLabel ? overflowMenuButtonTooltip : undefined,
      ariaLabel: !showOverflowButtonLabel ? overflowMenuButtonAriaLabel : undefined
    };
    const overflowMenuButtonProps = {
      ...buttonProps,
      variant: ButtonVariant.Icon,
      hideDownArrow: true,
      sections: [overflowMenuSection],
      onItemSelected: location => {
        onOverflowItemSelected(location[0][1]);
      },
      onItemClosed: location => onOverflowItemClosed(location[0][1])
    };
    overFlowBtnDropConnect(overflowMenuRef);
    return /*#__PURE__*/_jsx("div", {
      ref: e => {
        if (e) {
          overflowMenuRef.current = e;
          const size = isVerticalOriented ? e.getBoundingClientRect().height : e.getBoundingClientRect().width;
          setOverflowMenuSize(size);
        }
      },
      className: cx(cn.tabOverFlow, props.overflowMenuButtonDisplay && overflowMenuButtonPosition === 'endOfTabBar' && cn.endOfTabBar),
      children: props.renderOverflowMenuButton ? props.renderOverflowMenuButton(
      // button props
      buttonProps,
      // menu props
      {
        onItemSelected: onOverflowItemSelected,
        onItemClosed: onOverflowItemClosed,
        items: props.items,
        selectedIndex: props.selectedIndex
      }) : /*#__PURE__*/_jsx(MenuButton, {
        menuAlign: {
          horizontalAlign: 'end'
        },
        className: cn.tabOverflowButton,
        ...overflowMenuButtonProps
      })
    });
  }
  function shouldOverflowMenuBeDisplayed(overflowMenuDisplayType, isScrollVisible) {
    switch (overflowMenuDisplayType) {
      case 'always':
        return true;
      case 'onVisibleScrollbar':
        return isScrollVisible;
      default:
        return false;
    }
  }

  // renders all controls outside of the tabs section
  function renderNonTabsContent() {
    const displayOverflowMenu = shouldOverflowMenuBeDisplayed(props.overflowMenuButtonDisplay ?? 'never', areScrollButtonsVisible);
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [!isNullish(props.renderAddNewTabButton) && /*#__PURE__*/_jsx("div", {
        className: cn.addButtonContainer,
        children: props.renderAddNewTabButton({
          icon: {
            icon: AddTab
          }
        })
      }), isNullish(props.renderAddNewTabButton) && !isNullish(props.addNewTabButtonProps) && /*#__PURE__*/_jsx("div", {
        ref: e => {
          if (e) {
            addTabBtnRef.current = e;
            const size = isVerticalOriented ? e.getBoundingClientRect().height : e.getBoundingClientRect().width;
            setAddButtonSize(size);
          }
        },
        className: cn.addButtonContainer,
        children: /*#__PURE__*/_jsx(Button, {
          testId: postfixTestId(props.testId, 'addNewTabBtn'),
          icon: {
            icon: AddTab
          },
          variant: ButtonVariant.Light,
          className: cx(cn.addButton, isOverAddTabBtn && cn.dropZone),
          onPress: () => {
            props.addNewTabButtonProps?.onPress();
          },
          ...(props.addNewTabButtonProps.ariaLabel && orientation !== 'vertical' && !isTabbarIconOnly && !isButtonTextVisible && {
            ariaLabel: props.addNewTabButtonProps.ariaLabel
          }),
          ...(props.addNewTabButtonProps.title && orientation !== 'vertical' && !isTabbarIconOnly && !isButtonTextVisible && {
            tooltip: props.addNewTabButtonProps.title
          }),
          ...(orientation === 'vertical' && !isTabbarIconOnly && isButtonTextVisible && {
            text: localizer('tabBar.newTabButton.label')
          })
        })
      }), displayOverflowMenu && renderOverflowMenuButton(), isVerticalOriented && !isNullish(props.expandCollapseProps) && /*#__PURE__*/_jsx(Button, {
        testId: postfixTestId(props.testId, 'expandCollapseBtn'),
        className: cx(cn.expandCollapseButton, placement === 'end' ? cn.alignStart : cn.alignEnd, props.expandCollapseProps.isExpanded && cn.expanded),
        tooltip: props.expandCollapseProps.isExpanded ? expandTooltip : collapseTooltip,
        ariaLabel: props.expandCollapseProps.isExpanded ? expandAriaLabel : collapseAriaLabel,
        icon: {
          icon: placement === 'start' && !props.expandCollapseProps.isExpanded || placement === 'end' && props.expandCollapseProps.isExpanded ? MoveRight : MoveLeft
        },
        onPress: props.expandCollapseProps.onExpand,
        variant: ButtonVariant.Light
      })]
    });
  }
  function renderTabsContainer() {
    const isTabAllowDrag = props.items.every(item => item.allowDrag);
    const isDisplayContextMenu = shouldDisplayContextMenu;
    let ariaDescriptionText = '';
    if (isDisplayContextMenu && isTabAllowDrag) {
      ariaDescriptionText = `${localizer('tab.editLabelMessage.aria')} ${localizer('tabs.rearranged.message.aria')}`;
    } else if (isDisplayContextMenu) {
      ariaDescriptionText = localizer('tab.editLabelMessage.aria');
    } else if (isTabAllowDrag) {
      ariaDescriptionText = localizer('tabs.rearranged.message.aria');
    }

    // JSX for Tabs
    const tabsTemplate = props.items.map((item, index) => {
      return /*#__PURE__*/_jsx(InverseTokenProvider, {
        inverseTheme: index === props.selectedIndex && themeTransition && alternate || invertedThemeContext,
        children: item.allowDrag || props.allowDrop ? renderDraggableTab(index, item) : renderTab(index, item)
      }, index);
    });

    // JSX for TabBarContent
    const tabBarContent = /*#__PURE__*/_jsxs("div", {
      ref: e => {
        if (e) {
          tabBarRef.current = e;
          const size = isVerticalOriented ? e.querySelector('ul')?.getBoundingClientRect().height ?? 0 : isRTL ? e.querySelector('ul')?.getBoundingClientRect().left ?? 0 : e.querySelector('ul')?.getBoundingClientRect().right ?? 0;
          setScrollListSize(size);
          setTabBarSize(isRTL ? tabBarRef?.current?.getBoundingClientRect().right ?? 0 : tabBarRef?.current?.getBoundingClientRect().left ?? 0);
        }
      },
      style: {
        ...tokens,
        ...(props.allowDrop && dndTokens),
        ...(!isVerticalOriented && dropZoneAreaStartPosition),
        ...(isVerticalOriented && dropZoneAreaStartVerticalPosition),
        ...getStretchToFitTabBarItemWidth,
        ...props.style
      },
      className: cx(isVerticalOriented ? cn.vertical : cn.horizontal, isVerticalOriented && !isNullish(props.expandCollapseProps) && !props.expandCollapseProps.isExpanded && cn.collapsed, isVerticalOriented && placement === 'end' && cn.placeEnd, stretchToFit && !areScrollButtonsVisible && cn.stretchToFit, isOverNonScrollableZone && !isOverAddTabBtn && !isOverFlowMenu && !isOverTabBarScrollable && cn.droppable, (props.addNewTabButtonProps || props.overflowMenuButtonDisplay && overflowMenuButtonPosition === 'endOfTabList') && cn.tabListHeightAuto, props.className),
      role: role,
      "data-landmark-label": props.landmarkLabel,
      "data-landmark-initial-focus": tabIds[props.selectedIndex],
      children: [isVerticalOriented && props.headerElement?.(), !isVerticalOriented && renderTabToolbar && /*#__PURE__*/_jsx("div", {
        style: {
          width: props.toolbarWidth
        },
        className: cx(!(props.overflowMenuButtonDisplay && overflowMenuButtonPosition === 'endOfTabBar') && cn.toolbarInlineSpacing, cn.toolbar),
        children: renderTabToolbar()
      }), /*#__PURE__*/_jsx(BaseScrollable, {
        itemRefs: itemRefs,
        ref: e => {
          setRef(ref, e);
          tabBarScrollableRef.current = e;
        },
        acceptDropTypes: acceptDropTypes,
        orientation: orientation,
        currentIndex: horizontalScrollIndex,
        setCurrentIndex: setHorizontalScrollIndex,
        className: cx(cn.bar, props.className),
        contentClassName: cn.contentList,
        testId: props.testId,
        selectedIndex: props.selectedIndex,
        hideScrollButtons: isVerticalOriented,
        contentProps: {
          onKeyDown,
          onBlur,
          onFocus: () => {
            if (!tabItemHasFocus) {
              setTabItemHasFocus(true);
            }
            onFocus();
          },
          role: 'tablist',
          // 'aria-owns' is needed for JAWS to work properly because in our DOM,
          // "tab" elements are not direct children of "tablist" element.
          // See details in https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Tab_Role
          'aria-owns': tabIds.join(' '),
          'aria-orientation': orientation,
          'aria-label': props.ariaLabel,
          'aria-labelledby': props.ariaLabelledBy
        },
        contentAriaDescribedBy: ariaDescriptionText !== '' ? ariaDescriptionId : undefined,
        onOverflowChange: setScrollButtonVisibility,
        children: tabsTemplate
      }), /*#__PURE__*/_jsx(InvisibleText, {
        id: ariaDescriptionId,
        children: ariaDescriptionText
      }), renderNonTabsContent(), /*#__PURE__*/_jsx(TabDragPreview, {
        dragType: dragType,
        dndPrefix: dndPrefix,
        children: index => {
          return renderTab(index, props.items[index]);
        }
      })]
    });
    return tabBarContent;
  }
  function renderTab(index, item) {
    if (item === undefined) {
      return /*#__PURE__*/_jsx(Fragment, {});
    }
    return /*#__PURE__*/_jsx(Tab, {
      ref: createItemRef(index),
      altId: props.altId,
      ...item,
      id: tabIds[index],
      selected: index === props.selectedIndex,
      visualStyle: props.visualStyle,
      variant: props.variant,
      onPress: () => selectIfNeeded(index, item),
      onClose: () => {
        props.onTabClose?.(index);
      },
      isExpanded: props.expandCollapseProps?.isExpanded,
      onCloseAll: props.onTabCloseAll,
      testId: item.testId ?? postfixTestId(props.testId, `tab${index}`),
      stretchToFitTab: stretchToFit && !areScrollButtonsVisible,
      maxTabWidth: !isVerticalOriented ? props.maxTabWidth : undefined,
      closeTabButtonTooltip: props.closeTabButtonTooltip,
      closeTabButtonkeyboardTooltip: props.closeTabButtonkeyboardTooltip,
      hideCloseAllTabOption: !props.hideCloseAllTabOption && areAllTabsCloseable,
      showContextMenu: shouldDisplayContextMenu,
      menuButtonTooltip: props.items[index].menuButtonTooltip ?? props.menuButtonTooltip,
      location: alternate && !isVerticalOriented ? 'top' : isVerticalOriented ? placement === 'end' ? 'left' : 'right' : 'bottom',
      orientation: props.orientation,
      tokenOverrides: props.tabOverrides,
      ...(!isNullish(item.menuSections) && {
        menuProps: {
          menuSections: item.menuSections,
          onMenuItemSelect: (location, menuSections) => props.onTabMenuItemSelect?.(index, {
            ...item,
            menuSections
          }, location),
          onOpen: () => {
            props.onTabMenuOpen?.(index, item);
          }
        }
      }),
      closeTabMenuItemText: props.closeTabMenuItemText,
      closeAllTabMenuItemText: props.closeAllTabMenuItemText,
      renameMenuItemPosition: props.renameMenuItemPosition,
      showRenameMenuOption: props.showRenameMenuOption,
      showCloseMenuOption: props.showCloseMenuOption,
      focused: tabItemHasFocus && index === focusedIndex,
      preventInitialAutoFocus: preventInitialAutoFocus,
      setPreventInitialAutoFocus: setPreventInitialAutoFocus,
      tabIndex: isTabFocusable(index) ? 0 : -1,
      editable: props.editable,
      onLabelChange: (newValue, type) => props.onTabLabelChange?.(index, item, newValue, type),
      onFocus: () => {
        setHorizontalScrollIndex(index);
        if (index !== focusedIndex) {
          setFocusedIndex(index);
        }
      },
      allowDrag: item.allowDrag,
      movableTab: isItemsMovable
    }, index);
  }
  function renderDraggableTab(index, item) {
    return /*#__PURE__*/_jsx(DraggableTab, {
      id: tabIds[index],
      type: dragType,
      index: index,
      dndPrefix: dndPrefix,
      reorderTabs: props.reorderTabs,
      allowDrop: !!props.allowDrop,
      allowDrag: item.disabled === true ? false : !!item.allowDrag,
      acceptDropTypes: acceptDropTypes,
      droppableContent: droppableContent,
      onDragEnd: props.onDragEnd,
      onDrop: props.onDrop,
      onFocus: () => {
        setHorizontalScrollIndex(focusedIndex);
        if (index !== focusedIndex) {
          setFocusedIndex(focusedIndex);
        }
      },
      orientation: props.orientation,
      onDragOver: event => props.onDragOver?.(event),
      onDragLeave: event => props.onDragLeave?.(event),
      children: item && renderTab(index, item)
    });
  }
  return renderTabsContainer();
});
export const getTabBarDesignTokens = (dt, opts) => {
  const primaryLight = opts?.variant === TabBarVariant.PrimaryLight;
  let token;
  switch (dt.themeVariant) {
    case 'dark':
      token = {
        bd: !opts?.alternate ? dt.tokens['color-container-border-4'] : undefined
      };
      break;
    case 'high-contrast':
      token = {
        bd: dt.tokens['color-container-border-4']
      };
      break;
    default:
      if (primaryLight) {
        token = {
          bd: !opts?.alternate ? setAlpha(dt.tokens['color-action-icon-emphasis-normal'], 0.25) : undefined
        };
      } else {
        token = {
          bd: !opts?.alternate ? dt.tokens['color-container-border-4'] : undefined
        };
      }
  }
  const densityTokens = opts?.density === 'high' ? {
    'item-spacing': dt.tokens['space-squish-s'],
    'item-inline-start': dt.tokens['space-base-null']
  } : {
    'item-spacing': dt.tokens['space-squish-m'],
    'item-inline-start': dt.tokens['space-base-xs']
  };
  return {
    ...renderDesignTokens({
      ...token,
      ...densityTokens,
      'bd-bottom-width': dt.tokens['size-border-width-s'],
      'spacing-squish-s': dt.tokens['space-squish-s'],
      'spacing-squish-m': dt.tokens['space-squish-m'],
      'spacing-xs': dt.tokens['space-inline-xs'],
      'spacing-s': dt.tokens['space-inline-s'],
      'spacing-m': dt.tokens['space-base-m']
    }, 'tab-bar'),
    ...opts?.tokenOverrides?.(dt, {
      alternate: opts?.alternate,
      variant: opts?.variant
    }),
    ...(!dt.enableNovaTokens && getTabBarLegacyDesignTokens(dt, opts))
  };
};
export const getTabBarLegacyDesignTokens = (dt, opts) => {
  let token;
  switch (dt.themeVariant) {
    case 'dark':
      token = {
        bd: !opts?.alternate ? dt.c.white15 : undefined,
        'bd-bottom-width': !opts?.alternate ? '1px' : 0
      };
      break;
    case 'high-contrast':
      token = {
        bd: dt.c.white,
        'bd-bottom-width': '1px'
      };
      break;
    default:
      token = {
        bd: !opts?.alternate ? dt.c.n2 : undefined,
        'bd-bottom-width': !opts?.alternate ? '1px' : 0
      };
  }
  return {
    ...renderDesignTokens(token, 'tab-bar'),
    ...opts?.tokenOverrides?.(dt, {
      alternate: opts?.alternate,
      variant: opts?.variant
    })
  };
};

// Tabbar container border tokens and inline-styles as optional
// NOTE: Tabbar container is not part of Tabbar component it will defined at consumer end
export const getTabBarContainerBorderDesignTokens = (dt, opts) => {
  // Tabbar container inline-styles
  const inlineStyles = !opts?.ignoreInlineStyles ? {
    border: `${'var(--tab-bar-container-bd)'}`,
    borderBlockStartWidth: `${'var(--tab-bar-container-bd-top-width)'}`,
    background: `${'var(--tab-bar-container-bg)'}`,
    padding: `${'var(--tab-bar-container-pd)'}`
  } : undefined;
  return {
    ...renderDesignTokens({
      'container-pd': dt.tokens['space-base-m'],
      'container-bg': dt.tokens['color-container-background-0'],
      'container-bd-color': dt.tokens['color-container-border-4'],
      'container-bd-width': '1px',
      'container-bd-top-width': dt.themeVariant === 'high-contrast' ? 0 : '1px',
      'container-bd-type': 'solid',
      'container-bd': `${'var(--tab-bar-container-bd-width)'} ${'var(--tab-bar-container-bd-type)'} ${dt.tokens['color-container-border-4']}`
    }, 'tab-bar'),
    ...inlineStyles
  };
};
//# sourceMappingURL=BaseTabBar.js.map